import {
    contextRepository,
    eventRepository,
    gameRepository,
    playerRepository,
    playRepository,
    teamRepository
} from "../../storage/storage";
import { EventInfoSerializer } from "../serializers/legacy/football/EventInfoSerializer";
import DriveService from "../drive-service";
import FootballStatsService from "../football-stats-service";
import { TeamSerializer } from "../serializers/legacy/football/TeamSerializer";
import { PlayerSerializer } from "../serializers/legacy/football/PlayerSerializer";
import { DriveSerializer } from "../serializers/legacy/football/DriveSerializer";
import { PeriodStatSerializer } from "../serializers/legacy/football/PeriodStatSerializer";
import NoPlayer from "../../sports/common/players/NoPlayer";
import { PlaySerializer } from "../serializers/legacy/football/plays/PlaySerializer";
import { UpdateContextPlaySerializer } from "../serializers/legacy/football/plays/UpdateContextPlaySerializer";
import { ScoringSerializer } from "../serializers/legacy/football/ScoringSerializer";
import { BoxscoreService } from "./boxscore-service";
import FootballGameContext from "../../sports/football/context/football-game-context"; //Note: Order Matters

//Note: Order Matters
const playSerializers = [
    new UpdateContextPlaySerializer(),
    new PlaySerializer()
];

class FootballBoxscoreService extends BoxscoreService {
    getUpdatedContext(plays, play, gameContext) {
        const nextPlaySnapShot = plays[play.index]?.getContextSnapshot?.();
        return nextPlaySnapShot
            ? Object.assign(
                  new FootballGameContext(),
                  gameContext,
                  nextPlaySnapShot
              )
            : gameContext;
    }

    serializePlays(plays, serializationContext) {
        const { gameContext } = serializationContext;

        return plays
            .filter((p) => !!p.period)
            .reduce((acc, play) => {
                const { period } = play;
                if (!acc[period]) {
                    acc[period] = [];
                }

                const updatedSerializationContext = {
                    ...serializationContext,
                    isFirstPeriodPlay: !acc[period].length,
                    gameContext: this.getUpdatedContext(
                        plays,
                        play,
                        gameContext
                    )
                };

                const playSerializer = playSerializers.find((ps) =>
                    ps.applicable(play)
                );

                acc[period] = [
                    ...acc[period],
                    ...playSerializer.serialize(
                        play,
                        updatedSerializationContext
                    )
                ];

                return acc;
            }, {});
    }

    getTeamSerialization(team, teamStats, gameContext, lineScore, driveStats) {
        const teamSerialization = TeamSerializer.serialize(
            team,
            teamStats,
            gameContext
        );

        teamSerialization.players = teamStats.players
            .filter((p) => p.uniform !== NoPlayer.getUniform())
            .map((ps) => {
                const player = team.players.find((p) => p.id === ps.id);
                return PlayerSerializer.serialize(
                    player,
                    ps,
                    team,
                    gameContext
                );
            });

        const totalPeriods = Math.max(
            gameContext.currentPeriod,
            gameContext.gameConfig.general.periodsInRegulation
        );
        const periodNumbers = Array.from(
            { length: totalPeriods },
            (value, index) => index + 1
        );

        teamSerialization.periodstats = periodNumbers.map(
            (period, periodIndex) => {
                const periodStat = teamStats.periods[periodIndex];
                const periodScore = lineScore[period];
                const miscPossession = Math.round(
                    (driveStats || [])
                        .filter(({ drive }) => drive.startPeriod === period)
                        .reduce(
                            (acc, { drive }) => acc + (drive.totalTime || 0),
                            0
                        ) / 1000
                );

                return PeriodStatSerializer.serialize(
                    periodStat,
                    gameContext,
                    periodScore,
                    miscPossession
                );
            }
        );

        const firstDownTotals = teamSerialization.periodstats.reduce(
            (
                acc,
                {
                    firstDownNo,
                    firstDownPass,
                    firstDownPenalty,
                    firstDownRush,
                    miscPossession
                }
            ) => {
                return {
                    firstDownNo: acc.firstDownNo + firstDownNo,
                    firstDownPass: acc.firstDownPass + firstDownPass,
                    firstDownPenalty: acc.firstDownPenalty + firstDownPenalty,
                    firstDownRush: acc.firstDownRush + firstDownRush,
                    miscPossession: acc.miscPossession + miscPossession
                };
            },
            {
                firstDownNo: 0,
                firstDownPass: 0,
                firstDownPenalty: 0,
                firstDownRush: 0,
                miscPossession: 0
            }
        );

        teamSerialization.firstDownNo = firstDownTotals.firstDownNo;
        teamSerialization.firstDownPass = firstDownTotals.firstDownPass;
        teamSerialization.firstDownPenalty = firstDownTotals.firstDownPenalty;
        teamSerialization.firstDownRush = firstDownTotals.firstDownRush;
        teamSerialization.miscPossession = firstDownTotals.miscPossession;

        return teamSerialization;
    }

    async serialize(eventId) {
        const event = await eventRepository.getById(eventId);
        const game = await gameRepository.where({ eventId: eventId }).first();
        const plays = await playRepository.getPlaysWithFragments(game.id);
        const gameContext = await contextRepository
            .where({ gameId: game.id })
            .first();
        const { homeTeamId, awayTeamId } = gameContext;
        const homeTeam = await teamRepository.getById(homeTeamId);
        const awayTeam = await teamRepository.getById(awayTeamId);

        const [homePlayers, awayPlayers] = await Promise.all([
            playerRepository.where({ teamId: homeTeamId }).toArray(),
            playerRepository.where({ teamId: awayTeamId }).toArray()
        ]);

        homeTeam.players = homePlayers;
        awayTeam.players = awayPlayers;

        const { homeTeamStats, awayTeamStats, lineScore, driveStats } =
            await FootballStatsService.getBoxScore(game.id);

        const boxscorePlaysByQuarter = this.serializePlays(plays, {
            gameContext,
            homePlayers,
            awayPlayers,
            sportId: event.sportId,
            homeTeam,
            awayTeam
        });

        const awayTeamSerialize = this.getTeamSerialization(
            awayTeam,
            awayTeamStats,
            gameContext,
            lineScore.reduce((acc, { number, awayScore }) => {
                acc[number] = awayScore;
                return acc;
            }, {}),
            (driveStats || []).filter(
                ({ drive }) => drive.homeTeamHasPossession === false
            )
        );

        const homeTeamSerialize = this.getTeamSerialization(
            homeTeam,
            homeTeamStats,
            gameContext,
            lineScore.reduce((acc, { number, homeScore }) => {
                acc[number] = homeScore;
                return acc;
            }, {}),
            (driveStats || []).filter(
                ({ drive }) => drive.homeTeamHasPossession === true
            )
        );

        const drives = await DriveService.getByGameId(game.id);
        DriveService.computeDriveTotalTimes(drives, gameContext);
        const scoringDriveStats = await Promise.all(
            drives.map(async (drive) => ({
                drive,
                stats: await FootballStatsService.getByDriveId(
                    game.id,
                    drive.id
                )
            }))
        );

        const eventInfo = EventInfoSerializer.serialize(event, gameContext);
        eventInfo.scoring = ScoringSerializer.serialize(
            scoringDriveStats,
            plays,
            awayPlayers,
            homePlayers,
            gameContext
        );
        eventInfo.drives = drives.map((d) =>
            DriveSerializer.serialize(d, gameContext)
        );

        return {
            countPeriods: 0,
            eventInfo,
            gamePeriods: 0,
            plays: boxscorePlaysByQuarter,
            teams: [awayTeamSerialize, homeTeamSerialize]
        };
    }
}

export default new FootballBoxscoreService();
